/**
 * Web mode bootstrap — call this once before React renders.
 *
 * 1. Fetches /api/config to get the public env block the main process would
 *    normally send via IPC (Mapbox tokens, table names, Cognito config, etc.)
 * 2. Populates every window.* global that Electron's contextBridge normally
 *    exposes, so all renderer components that call window.fleetAPI,
 *    window.eventsAPI, etc. work without modification.
 */

import { createWebAuthApi } from './webAuthApi';
import { createWebEventsApi } from './webEventsApi';
import { createWebFleetApi } from './webFleetApi';
import { createWebSimApi } from './webSimApi';
import { createWebSerialApi } from './webSerialApi';
import { createWebLogApi } from './webLogApi';
import { createWebErrorApi } from './webErrorApi';
import { createWebWindowApi } from './webWindowApi';
import type { FmPublicEnv } from '../../types/FmPublicEnv';

export async function setupWebGlobals(): Promise<void> {
  // Fetch public env from server (equivalent to what main process sends via IPC)
  const configRes = await fetch('/api/config', { credentials: 'include' });
  if (!configRes.ok) {
    throw new Error(`Failed to load /api/config: ${configRes.status}`);
  }
  const config = (await configRes.json()) as FmPublicEnv;

  // The global.d.ts Window interface declares these — we cast through unknown
  // where our web return types are wider (Promise<unknown>) than the declared types.
  window.fmPublicEnv = config;
  window.authAPI = createWebAuthApi();
  window.eventsAPI = createWebEventsApi() as unknown as typeof window.eventsAPI;
  window.fleetAPI = createWebFleetApi() as unknown as typeof window.fleetAPI;
  window.simAPI = createWebSimApi() as unknown as typeof window.simAPI;
  window.serialAPI = createWebSerialApi() as unknown as typeof window.serialAPI;
  window.logAPI = createWebLogApi();
  window.errorAPI = createWebErrorApi();
  window.windowAPI = createWebWindowApi();

  // Electron has a loginWindowAPI for closing the popup; in web mode this is a no-op
  (window as unknown as { loginWindowAPI: { close: () => void } }).loginWindowAPI = {
    close: () => {},
  };
}
